import { useEffect, useMemo, useState, type PointerEvent } from "react";
import type {
  PublicAnalytics,
  PublicEquity,
  PublicSummary,
} from "../../contracts/src/index.ts";
import {
  buildAlignedPerformanceSeries,
  buildAmountSeries,
  buildAxisTicks,
  buildCashFlowEvents,
  buildTimeTicks,
  resolveEndLabelPositions,
  type ChartMode,
  type ChartSeriesPoint,
  type PeriodKey,
} from "./chart-model.ts";
import { formatDate, formatMoney, formatPercent } from "./charts.tsx";

interface TooltipState {
  left: number;
  top: number;
  title: string;
  rows: Array<[string, string]>;
  note?: string;
}

interface AccountChartProps {
  equity: PublicEquity | null;
  summary: PublicSummary | null;
  currency: string;
  mode: ChartMode;
  period: PeriodKey;
  compact?: boolean;
}

interface ChartLayout {
  width: number;
  height: number;
  margin: { left: number; right: number; top: number; bottom: number };
  plotWidth: number;
  plotHeight: number;
}

function createLayout(
  width: number,
  height: number,
  margin: ChartLayout["margin"],
): ChartLayout {
  return {
    width,
    height,
    margin,
    plotWidth: width - margin.left - margin.right,
    plotHeight: height - margin.top - margin.bottom,
  };
}

const DESKTOP_LAYOUT = createLayout(1200, 470, {
  left: 105,
  right: 220,
  top: 28,
  bottom: 64,
});
const MOBILE_LAYOUT = createLayout(720, 470, {
  left: 105,
  right: 220,
  top: 28,
  bottom: 64,
});

function useChartLayout(): ChartLayout {
  const [narrow, setNarrow] = useState(false);
  useEffect(() => {
    const media = window.matchMedia("(max-width: 768px)");
    const sync = () => setNarrow(media.matches);
    queueMicrotask(sync);
    media.addEventListener("change", sync);
    return () => media.removeEventListener("change", sync);
  }, []);
  return narrow ? MOBILE_LAYOUT : DESKTOP_LAYOUT;
}

function sample(points: ChartSeriesPoint[], target = 420): ChartSeriesPoint[] {
  if (points.length <= target) return points;
  const step = Math.ceil(points.length / target);
  const sampled = points.filter((_, index) => index % step === 0);
  const last = points.at(-1);
  if (last && sampled.at(-1) !== last) sampled.push(last);
  return sampled;
}

function pathFor(
  points: ChartSeriesPoint[],
  min: number,
  max: number,
  from: number,
  to: number,
  layout: ChartLayout,
): string {
  const spread = max - min || 1;
  const timeSpread = to - from || 1;
  return points
    .map((point, index) => {
      const x =
        layout.margin.left +
        ((point.recordedAt - from) / timeSpread) * layout.plotWidth;
      const y =
        layout.margin.top +
        layout.plotHeight -
        ((point.value - min) / spread) * layout.plotHeight;
      return `${index === 0 ? "M" : "L"}${x.toFixed(2)},${y.toFixed(2)}`;
    })
    .join(" ");
}

function pointPosition(
  point: ChartSeriesPoint,
  min: number,
  max: number,
  from: number,
  to: number,
  layout: ChartLayout,
) {
  return {
    x:
      layout.margin.left +
      ((point.recordedAt - from) / (to - from || 1)) * layout.plotWidth,
    y:
      layout.margin.top +
      layout.plotHeight -
      ((point.value - min) / (max - min || 1)) * layout.plotHeight,
  };
}

function tooltipPosition(
  x: number,
  y: number,
  layout: ChartLayout,
): Pick<TooltipState, "left" | "top"> {
  return {
    left: Math.min(82, Math.max(8, (x / layout.width) * 100)),
    top: Math.min(76, Math.max(8, (y / layout.height) * 100)),
  };
}

function ChartTooltip({ tooltip }: { tooltip: TooltipState }) {
  return (
    <aside
      className="chart-tooltip"
      style={{ left: `${tooltip.left}%`, top: `${tooltip.top}%` }}
      role="status"
    >
      <strong>{tooltip.title}</strong>
      <dl>
        {tooltip.rows.map(([label, value]) => (
          <div key={label}>
            <dt>{label}</dt>
            <dd>{value}</dd>
          </div>
        ))}
      </dl>
      {tooltip.note ? <p>{tooltip.note}</p> : null}
    </aside>
  );
}

export function AccountChart({
  equity,
  summary,
  currency,
  mode,
  period,
  compact = false,
}: AccountChartProps) {
  const layout = useChartLayout();
  const [tooltip, setTooltip] = useState<TooltipState | null>(null);
  const points = useMemo(
    () =>
      mode === "amount"
        ? buildAmountSeries(equity)
        : buildAlignedPerformanceSeries(equity),
    [equity, mode],
  );
  const grouped = useMemo(
    () => ({
      historical: sample(
        points.filter((point) => point.series === "historical"),
      ),
      liveBalance: sample(
        points.filter((point) => point.series === "liveBalance"),
      ),
      liveEquity: sample(
        points.filter((point) => point.series === "liveEquity"),
      ),
    }),
    [points],
  );
  const cashFlowEvents = useMemo(
    () =>
      mode === "amount"
        ? buildCashFlowEvents(equity?.historical_balance.points ?? [])
        : [],
    [equity, mode],
  );

  if (!points.length) {
    return (
      <div className="chart-empty" role="status">
        <span className="chart-empty-mark" aria-hidden="true" />
        <strong>尚無可繪製的績效資料</strong>
        <span>
          Snapshot 或 deals
          完成上傳後，這裡會顯示真實資料；系統不會產生示意績效。
        </span>
      </div>
    );
  }

  const timestamps = points.map((point) => point.recordedAt);
  const from = Math.min(...timestamps);
  const to = Math.max(...timestamps);
  const amountReferences =
    mode === "amount"
      ? [
          equity?.initialBalance?.balance,
          summary?.balance,
          summary?.equity,
        ].filter(
          (value): value is number =>
            value !== null && value !== undefined && Number.isFinite(value),
        )
      : [];
  const axis = buildAxisTicks([
    ...points.map((point) => point.value),
    ...amountReferences,
  ]);
  if (!axis) return null;
  const ticks = buildTimeTicks(from, to, period);
  const formatter =
    mode === "amount"
      ? (value: number) => formatMoney(value, currency)
      : (value: number) => formatPercent(value, true);
  const endpoints =
    mode === "amount" && summary
      ? resolveEndLabelPositions(
          [
            { key: "Balance", value: summary.balance },
            { key: "Equity", value: summary.equity },
          ],
          axis.min,
          axis.max,
          layout.plotHeight,
          34,
        )
      : [];
  const openPointTooltip = (point: ChartSeriesPoint) => {
    const position = pointPosition(point, axis.min, axis.max, from, to, layout);
    const rows: Array<[string, string]> =
      mode === "amount"
        ? [
            [
              point.series === "liveEquity" ? "Equity" : "Balance",
              formatMoney(point.value, currency),
            ],
            [
              "當日交易損益",
              point.tradingPnl === undefined
                ? "N/A"
                : formatMoney(point.tradingPnl, currency),
            ],
            [
              "當日外部資金流",
              point.cashFlow === undefined
                ? "N/A"
                : formatMoney(point.cashFlow, currency),
            ],
          ]
        : [
            ["現金流中性累積報酬", formatPercent(point.value, true)],
            [
              "當日交易損益",
              point.tradingPnl === undefined
                ? "N/A"
                : formatMoney(point.tradingPnl, currency),
            ],
            [
              "當日外部資金流",
              point.cashFlow === undefined
                ? "N/A"
                : formatMoney(point.cashFlow, currency),
            ],
          ];
    setTooltip({
      ...tooltipPosition(position.x, position.y, layout),
      title: formatDate(point.recordedAt),
      rows,
      note:
        mode === "amount" && point.cashFlow
          ? "外部資金異動，不計入交易報酬。"
          : undefined,
    });
  };
  const closeIfBackground = (event: PointerEvent<HTMLDivElement>) => {
    if (!(event.target as Element).closest("[data-chart-interactive]"))
      setTooltip(null);
  };

  return (
    <div
      className={`account-chart-shell${compact ? " compact" : ""}`}
      onPointerDown={closeIfBackground}
    >
      <svg
        className="account-chart"
        viewBox={`0 0 ${layout.width} ${layout.height}`}
        role="img"
        aria-label={
          mode === "amount"
            ? "帳戶金額曲線與資金事件"
            : "現金流中性累積績效曲線"
        }
      >
        <defs>
          <linearGradient
            id={`hq-live-area-${mode}`}
            x1="0"
            y1="0"
            x2="0"
            y2="1"
          >
            <stop offset="0%" stopColor="#3ee7bd" stopOpacity="0.2" />
            <stop offset="100%" stopColor="#3ee7bd" stopOpacity="0" />
          </linearGradient>
        </defs>

        {axis.ticks.map((tick) => {
          const y =
            layout.margin.top +
            layout.plotHeight -
            ((tick - axis.min) / (axis.max - axis.min || 1)) *
              layout.plotHeight;
          return (
            <g key={tick}>
              <line
                x1={layout.margin.left}
                x2={layout.margin.left + layout.plotWidth}
                y1={y}
                y2={y}
                className="chart-grid"
              />
              <text
                x={layout.margin.left - 14}
                y={y + 4}
                textAnchor="end"
                className="chart-axis-label"
              >
                {formatter(tick)}
              </text>
            </g>
          );
        })}
        {ticks.map((tick) => {
          const x =
            layout.margin.left +
            ((tick.recordedAt - from) / (to - from || 1)) * layout.plotWidth;
          return (
            <g key={`${tick.kind}-${tick.recordedAt}`}>
              <line
                x1={x}
                x2={x}
                y1={layout.margin.top}
                y2={layout.margin.top + layout.plotHeight}
                className={`chart-month-line ${tick.kind}`}
              />
              <text
                x={x}
                y={layout.height - 22}
                textAnchor="middle"
                className="chart-time-label"
              >
                {tick.label}
              </text>
            </g>
          );
        })}

        {mode === "amount" && equity?.initialBalance
          ? (() => {
              const y =
                layout.margin.top +
                layout.plotHeight -
                ((equity.initialBalance.balance - axis.min) /
                  (axis.max - axis.min || 1)) *
                  layout.plotHeight;
              return (
                <g className="initial-balance-reference">
                  <line
                    x1={layout.margin.left}
                    x2={layout.margin.left + layout.plotWidth}
                    y1={y}
                    y2={y}
                  />
                  <text
                    x={layout.margin.left + 8}
                    y={y - 8}
                  >{`初始金額 ${formatMoney(equity.initialBalance.balance, currency)} · ${formatDate(equity.initialBalance.recordedAt)}`}</text>
                </g>
              );
            })()
          : null}

        {equity?.firstActualTradeAt &&
        equity.firstActualTradeAt >= from &&
        equity.firstActualTradeAt <= to
          ? (() => {
              const x =
                layout.margin.left +
                ((equity.firstActualTradeAt - from) / (to - from || 1)) *
                  layout.plotWidth;
              return (
                <g className="inception-marker">
                  <line
                    x1={x}
                    x2={x}
                    y1={layout.margin.top}
                    y2={layout.margin.top + layout.plotHeight}
                  />
                  <text x={x + 8} y={layout.margin.top + 16}>
                    績效追蹤自 {formatDate(equity.firstActualTradeAt).slice(5)}{" "}
                    開始
                  </text>
                </g>
              );
            })()
          : null}

        {mode === "amount"
          ? cashFlowEvents.map((event) => {
              const x =
                layout.margin.left +
                ((event.recordedAt - from) / (to - from || 1)) *
                  layout.plotWidth;
              const y =
                layout.margin.top +
                layout.plotHeight -
                ((event.balance - axis.min) / (axis.max - axis.min || 1)) *
                  layout.plotHeight;
              const tone =
                event.cashIn > 0 && event.cashOut > 0
                  ? "mixed"
                  : event.netCashFlow >= 0
                    ? "deposit"
                    : "withdrawal";
              const eventLabel =
                tone === "mixed"
                  ? `入金 ${formatMoney(event.cashIn, currency)}／出金 ${formatMoney(event.cashOut, currency)}`
                  : event.netCashFlow >= 0
                    ? `入金 +${formatMoney(event.cashIn, currency)}`
                    : `出金 -${formatMoney(event.cashOut, currency)}`;
              return (
                <g
                  key={event.recordedAt}
                  className={`cash-flow-marker ${tone}`}
                  tabIndex={0}
                  role="button"
                  data-chart-interactive="true"
                  onClick={(clickEvent) => {
                    clickEvent.stopPropagation();
                    setTooltip({
                      ...tooltipPosition(x, y, layout),
                      title: `資金異動 · ${formatDate(event.recordedAt)}`,
                      rows: [
                        ["當日累計入金", formatMoney(event.cashIn, currency)],
                        ["當日累計出金", formatMoney(event.cashOut, currency)],
                        [
                          "當日淨資金流",
                          formatMoney(event.netCashFlow, currency),
                        ],
                        [
                          "資金異動時間",
                          event.times
                            .map((time) =>
                              new Date(time * 1000).toISOString().slice(11, 19),
                            )
                            .join("、"),
                        ],
                      ],
                      note: "外部資金異動，不計入交易報酬。",
                    });
                  }}
                >
                  <line
                    x1={x}
                    x2={x}
                    y1={layout.margin.top}
                    y2={layout.margin.top + layout.plotHeight}
                  />
                  <circle cx={x} cy={y} r="8" />
                  <text x={x + 9} y={Math.max(layout.margin.top + 12, y - 10)}>
                    {eventLabel}
                  </text>
                </g>
              );
            })
          : null}

        {Object.entries(grouped).map(([series, seriesPoints]) => {
          if (!seriesPoints.length) return null;
          const path = pathFor(
            seriesPoints,
            axis.min,
            axis.max,
            from,
            to,
            layout,
          );
          const className =
            series === "historical"
              ? "historical-balance-line"
              : series === "liveBalance"
                ? "live-balance-line"
                : "live-equity-line";
          return (
            <g key={series}>
              <path d={path} className={className} />
              {sample(seriesPoints, 36).map((point) => {
                const { x, y } = pointPosition(
                  point,
                  axis.min,
                  axis.max,
                  from,
                  to,
                  layout,
                );
                return (
                  <circle
                    key={`${series}-${point.recordedAt}`}
                    cx={x}
                    cy={y}
                    r="11"
                    className="chart-hit"
                    tabIndex={0}
                    role="button"
                    data-chart-interactive="true"
                    onClick={(event) => {
                      event.stopPropagation();
                      openPointTooltip(point);
                    }}
                  >
                    <title>{`${formatDate(point.recordedAt)} · ${formatter(point.value)}`}</title>
                  </circle>
                );
              })}
            </g>
          );
        })}

        {endpoints.map((label) => {
          const actualY =
            layout.margin.top +
            layout.plotHeight -
            ((label.value - axis.min) / (axis.max - axis.min || 1)) *
              layout.plotHeight;
          const labelY = layout.margin.top + label.y;
          return (
            <g
              key={label.key}
              className={`chart-end-label ${label.key.toLowerCase()}`}
              tabIndex={0}
              role="button"
              data-chart-interactive="true"
              onClick={(event) => {
                event.stopPropagation();
                setTooltip({
                  ...tooltipPosition(
                    layout.margin.left + layout.plotWidth,
                    labelY,
                    layout,
                  ),
                  title: `目前 ${label.key}`,
                  rows: [
                    ["Balance", formatMoney(summary!.balance, currency)],
                    ["Equity", formatMoney(summary!.equity, currency)],
                    [
                      "Floating P/L",
                      formatMoney(summary!.equity - summary!.balance, currency),
                    ],
                    [
                      "最後更新",
                      new Date(summary!.lastUpdatedAt * 1000).toISOString(),
                    ],
                  ],
                });
              }}
            >
              <path
                d={`M${layout.margin.left + layout.plotWidth},${actualY} L${layout.margin.left + layout.plotWidth + 18},${labelY} L${layout.margin.left + layout.plotWidth + 26},${labelY}`}
              />
              <rect
                x={layout.margin.left + layout.plotWidth + 26}
                y={labelY - 16}
                width="176"
                height="32"
                rx="7"
              />
              <text
                x={layout.margin.left + layout.plotWidth + 36}
                y={labelY + 5}
              >{`目前 ${label.key} ${formatMoney(label.value, currency)}`}</text>
            </g>
          );
        })}
      </svg>
      {tooltip ? <ChartTooltip tooltip={tooltip} /> : null}
      {!compact ? (
        <div className="chart-accessible-summary">
          {mode === "amount" ? (
            <>
              <span>
                <i className="legend-dot historical" />
                Historical Balance
              </span>
              <span>
                <i className="legend-dot balance" />
                Live Balance
              </span>
              <span>
                <i className="legend-dot equity" />
                Live Equity
              </span>
              <span className="chart-baseline-source">
                初始基準：
                {equity?.initialBalance
                  ? `${formatDate(equity.initialBalance.recordedAt)} · ${equity.initialBalance.source}`
                  : "N/A（缺少可靠基準）"}
              </span>
            </>
          ) : (
            <>
              <span>
                <i className="legend-dot historical" />
                Historical cash-flow-neutral
              </span>
              <span>
                <i className="legend-dot equity" />
                Live cash-flow-neutral
              </span>
            </>
          )}
        </div>
      ) : null}
    </div>
  );
}

export function StrategyContributionChart({
  analytics,
  currency,
}: {
  analytics: PublicAnalytics | null;
  currency: string;
}) {
  const layout = useChartLayout();
  const contribution = analytics?.strategyContribution;
  if (!contribution?.points.length)
    return <div className="mini-empty">此策略尚無可繪製的已完成交易貢獻</div>;
  const values = contribution.points.map((point) => point.cumulativePnl);
  const axis = buildAxisTicks(values);
  if (!axis) return null;
  const from = contribution.points[0].recordedAt;
  const to = contribution.points.at(-1)!.recordedAt;
  const path = contribution.points
    .map((point, index) => {
      const x =
        layout.margin.left +
        ((point.recordedAt - from) / (to - from || 1)) * layout.plotWidth;
      const y =
        layout.margin.top +
        layout.plotHeight -
        ((point.cumulativePnl - axis.min) / (axis.max - axis.min || 1)) *
          layout.plotHeight;
      return `${index === 0 ? "M" : "L"}${x},${y}`;
    })
    .join(" ");
  return (
    <div className="account-chart-shell strategy-contribution-chart">
      <svg
        className="account-chart"
        viewBox={`0 0 ${layout.width} ${layout.height}`}
        role="img"
        aria-label="策略累積損益貢獻曲線"
      >
        {axis.ticks.map((tick) => {
          const y =
            layout.margin.top +
            layout.plotHeight -
            ((tick - axis.min) / (axis.max - axis.min || 1)) *
              layout.plotHeight;
          return (
            <g key={tick}>
              <line
                x1={layout.margin.left}
                x2={layout.margin.left + layout.plotWidth}
                y1={y}
                y2={y}
                className="chart-grid"
              />
              <text
                x={layout.margin.left - 14}
                y={y + 4}
                textAnchor="end"
                className="chart-axis-label"
              >
                {formatMoney(tick, currency)}
              </text>
            </g>
          );
        })}
        <path d={path} className="strategy-contribution-line" />
        <text
          x={layout.margin.left}
          y={layout.height - 22}
          className="chart-time-label"
        >
          {formatDate(from)}
        </text>
        <text
          x={layout.margin.left + layout.plotWidth}
          y={layout.height - 22}
          textAnchor="end"
          className="chart-time-label"
        >
          {formatDate(to)}
        </text>
      </svg>
      <div className="chart-accessible-summary">
        <span>
          累積貢獻 {formatMoney(contribution.cumulativePnl, currency)}
        </span>
        <span>
          相對帳戶初始資金{" "}
          {formatPercent(contribution.points.at(-1)?.contributionPct, true)}
        </span>
        <span>此曲線不是獨立策略 Balance 或獨立策略 ROI。</span>
      </div>
    </div>
  );
}
